<template>
  <div
    ref="rootRef"
    :class="[$style.root, { [$style.disabled]: disabled }]"
    role="group"
    :test-id="testId"
    @focusout="onFocusOut"
    @mousedown="onRootMouseDown"
  >
    <template v-for="(part, index) in parts" :key="`${format}-${index}`">
      <span v-if="part.kind === 'literal'" :class="$style.literal" aria-hidden="true">{{ part.text }}</span>
      <span v-else :class="$style.sectionWrap">
        <!-- The visible section: unchanged text, carrying the layout. The input over it renders nothing. -->
        <span :class="[$style.section, { [$style.empty]: values[part.type] == null }]" aria-hidden="true">{{
          sectionText(part)
        }}</span>
        <input
          :ref="(el) => setSectionRef(index, el as HTMLElement | null)"
          type="text"
          role="spinbutton"
          :tabindex="disabled ? -1 : 0"
          :readonly="disabled || readonly"
          :class="$style.sectionInput"
          :aria-label="t(`datetimePicker.sectionNames.${part.type}`)"
          :aria-valuemin="getSectionBounds(part, values).min"
          :aria-valuemax="getSectionBounds(part, values).max"
          :aria-valuenow="values[part.type] ?? undefined"
          :aria-valuetext="
            part.type === 'month' && part.monthStyle && values[part.type] != null ? sectionText(part) : undefined
          "
          :aria-invalid="invalid || undefined"
          :test-id="`datetime-section-${part.type}`"
          :inputmode="inputModeFor(part)"
          autocomplete="off"
          @focus="onSectionFocus(index)"
          @keydown="onSectionKeydown($event, part, index)"
          @beforeinput="onSectionBeforeInput($event as InputEvent)"
          @input="onSectionInput($event)"
        />
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { resolveDateFnsLocale } from "@scayla/core";
import { format as formatWithDateFns } from "date-fns";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { FormatSection, MonthStyle, SectionType, SectionValues } from "./dateSectionUtils";
import {
  clampToBounds,
  composeDate,
  dateToSectionValues,
  expandTwoDigitYear,
  formatSections,
  formatSectionValue,
  getCompletionState,
  getSectionBounds,
  matchMonthName,
  parseFormatParts,
  stepValue,
  typeDigit,
} from "./dateSectionUtils";

/**
 * Masked, section-based date/time editor. Each part of the user's
 * configured format (day, month, year, hours, …) is an independently editable
 * section: typing replaces the focused section and auto-advances, arrows
 * step values, Tab / arrow keys / clicks move between sections. Empty
 * sections show the expected format as a hint.
 *
 * A section is drawn by a span, with a transparent `<input>` laid over it that takes
 * the focus: only a real form control reliably raises an on-screen keyboard, and
 * keeping it invisible leaves the rendering to the span. Nothing is ever typed into
 * it — every `beforeinput` is cancelled and routed through the same section logic
 * as a key.
 *
 * The model only ever receives complete, valid values; while an entry is
 * partial the model is `null`. Impossible combinations (31 February) and
 * abandoned partial entries flip `v-model:invalid` once editing finishes
 * (focus leaves the editor) and clear it as soon as the value is fixed.
 */

const props = withDefaults(
  defineProps<{
    /** Full date-fns display pattern, precision already applied (e.g. `dd.MM.yyyy HH:mm`). */
    format: string;
    disabled?: boolean;
    readonly?: boolean;
    testId?: string;
  }>(),
  {
    disabled: false,
    readonly: false,
    testId: undefined,
  },
);

const model = defineModel<Date | null>({ default: null });
const invalid = defineModel<boolean>("invalid", { default: false });

const emit = defineEmits<{
  /** Forward navigation ran past the last section — a parent (e.g. a range) can move focus onward. */
  next: [];
  /** Backward navigation ran before the first section — a parent can move focus back. */
  prev: [];
}>();

const { t, locale } = useI18n();

const parts = computed(() => parseFormatParts(props.format));
const sections = computed(() => formatSections(parts.value));
const hasMeridiem = computed(() => sections.value.some((s) => s.type === "meridiem"));

/** date-fns locale matching the active app locale, for localized month names. */
const dateFnsLocale = computed(() => resolveDateFnsLocale(locale.value));
/** Localized month names (index 0 = January), used to render and type textual months. */
function monthNamesFor(token: "MMMM" | "MMM"): string[] {
  return Array.from({ length: 12 }, (_, i) =>
    formatWithDateFns(new Date(2000, i, 1), token, { locale: dateFnsLocale.value }),
  );
}
const monthNamesLong = computed(() => monthNamesFor("MMMM"));
const monthNamesShort = computed(() => monthNamesFor("MMM"));
/** Names for a textual month section's configured style (used for display). */
function displayMonthNames(style: MonthStyle): string[] {
  return style === "long" ? monthNamesLong.value : monthNamesShort.value;
}

const values = ref<SectionValues>({});
/** Digits typed into the active section during the current burst. */
const typedCount = ref(0);
/** Letters typed into a textual-month section during the current burst. */
const typedText = ref("");
const activeIndex = ref<number | null>(null);

const rootRef = ref<HTMLElement>();
const sectionEls = new Map<number, HTMLElement>();

function setSectionRef(index: number, el: HTMLElement | null) {
  if (el) sectionEls.set(index, el);
  else sectionEls.delete(index);
}

const SECTION_TYPES: readonly SectionType[] = [
  "day",
  "month",
  "year",
  "hours",
  "minutes",
  "seconds",
  "milliseconds",
  "meridiem",
];

function sectionValuesEqual(a: SectionValues, b: SectionValues): boolean {
  return SECTION_TYPES.every((type) => (a[type] ?? null) === (b[type] ?? null));
}

/**
 * Timestamp of the last value we emitted (`null` = we emitted a cleared value,
 * `undefined` = the current display was synced from outside). Used to ignore
 * our own emit echoing back through the parent.
 */
let lastEmitted: number | null | undefined = undefined;

function setModel(value: Date | null) {
  lastEmitted = value?.getTime() ?? null;
  model.value = value;
}

function resetFromModel() {
  values.value = model.value ? dateToSectionValues(model.value, sections.value) : {};
  typedCount.value = 0;
  typedText.value = "";
  invalid.value = false;
  lastEmitted = undefined;
}

watch(
  model,
  (incoming) => {
    if ((incoming?.getTime() ?? null) === lastEmitted) return;
    // Ignore parent updates during typing.
    if (activeIndex.value !== null && (typedCount.value > 0 || typedText.value !== "")) return;
    // Serialize/parse round-trips can shift the timestamp without changing any
    // displayed section (e.g. date-only values) — don't clobber typing state then.
    if (incoming && sectionValuesEqual(dateToSectionValues(incoming, sections.value), values.value)) {
      lastEmitted = undefined;
      return;
    }
    resetFromModel();
  },
  { immediate: true },
);

watch(sections, resetFromModel);

const allFilled = computed(() => sections.value.every((s) => values.value[s.type] != null));

/** Commit while typing: only fully-entered values sync out (keeps the calendar live). */
function commitLive() {
  if (allFilled.value) {
    const date = composeDate(sections.value, values.value);
    if (date) {
      invalid.value = false;
      setModel(date);
      return;
    }
  }
  if (getCompletionState(sections.value, values.value) === "empty") invalid.value = false;
  if (model.value !== null) setModel(null);
}

/**
 * Editing finished (focus left the editor): apply the forgiving rules — empty
 * time sections default to 00, short years expand — then either commit the
 * normalized value or flag the entry as invalid.
 */
function finalize() {
  activeIndex.value = null;
  typedCount.value = 0;
  typedText.value = "";
  const state = getCompletionState(sections.value, values.value);
  if (state === "empty") {
    invalid.value = false;
    if (model.value !== null) setModel(null);
    return;
  }
  if (state === "complete") {
    const date = composeDate(sections.value, values.value);
    if (date) {
      // Normalize the display: windowed years, defaulted time parts.
      values.value = dateToSectionValues(date, sections.value);
      invalid.value = false;
      setModel(date);
      return;
    }
  }
  invalid.value = true;
}

function sectionText(section: FormatSection): string {
  const value = values.value[section.type];
  if (value != null) {
    const names = section.type === "month" && section.monthStyle ? displayMonthNames(section.monthStyle) : undefined;
    return formatSectionValue(section, value, names);
  }
  return placeholderFor(section);
}

function placeholderFor(section: FormatSection): string {
  if (section.type === "meridiem") return "AM";
  // Textual month: hint with the token itself (e.g. `MMMM`) — it expects a name, not digits.
  if (section.type === "month" && section.monthStyle) return section.token;
  if (section.type === "day" || section.type === "month" || section.type === "year") {
    return t(`datetimePicker.sectionChars.${section.type}`).repeat(section.padLength);
  }
  // Time tokens (HH / hh / mm / ss) already read as their own hint.
  return section.token[0].repeat(section.padLength);
}

function setValue(type: SectionType, value: number | null) {
  values.value = { ...values.value, [type]: value };
}

/**
 * Tidy a section when the user leaves it: clamp below-minimum values and
 * expand short years (`26` → `2026`, `85` → `1985`).
 */
function normalizeSection(index: number) {
  const part = parts.value[index];
  if (part?.kind !== "section") return;
  const value = values.value[part.type];
  if (value == null) return;
  const bounds = getSectionBounds(part, values.value);
  let next: number;
  if (part.type === "year" && part.maxDigits === 4 && value > 0 && value < 100) next = expandTwoDigitYear(value);
  else next = clampToBounds(value, bounds);
  if (next !== value) {
    setValue(part.type, next);
    commitLive();
  }
}

function onSectionFocus(index: number) {
  if (activeIndex.value !== null && activeIndex.value !== index) normalizeSection(activeIndex.value);
  if (activeIndex.value !== index) {
    typedCount.value = 0;
    typedText.value = "";
  }
  activeIndex.value = index;
}

function onFocusOut(event: FocusEvent) {
  const next = event.relatedTarget as Node | null;
  if (next && rootRef.value?.contains(next)) return;
  finalize();
}

function sectionIndexes(): number[] {
  return parts.value.flatMap((part, index) => (part.kind === "section" ? [index] : []));
}

function focusSectionAt(index: number) {
  sectionEls.get(index)?.focus();
}

function focusSibling(index: number, direction: 1 | -1) {
  const indexes = sectionIndexes();
  const position = indexes.indexOf(index);
  const target = indexes[position + direction];
  if (target !== undefined) focusSectionAt(target);
  // No section left in this direction — let a parent move focus across editors (range start ↔ end).
  else if (direction === 1) emit("next");
  else emit("prev");
}

function focusFirstEditable() {
  const indexes = sectionIndexes();
  const firstEmpty = indexes.find((i) => {
    const part = parts.value[i];
    return part.kind === "section" && values.value[part.type] == null;
  });
  const target = firstEmpty ?? indexes[0];
  if (target !== undefined) focusSectionAt(target);
}

function focusLastEditable() {
  const indexes = sectionIndexes();
  const target = indexes[indexes.length - 1];
  if (target !== undefined) focusSectionAt(target);
}

/** Keys that read as format separators — typing one finishes the section. */
const SEPARATOR_KEYS = [".", ",", "/", "-", ":", " "];

function onSectionKeydown(event: KeyboardEvent, section: FormatSection, index: number) {
  if (props.disabled || props.readonly) return;
  const key = event.key;

  if (key === "Enter") {
    finalize();
    activeIndex.value = index;
    return;
  }
  if (key === "ArrowLeft" || key === "ArrowRight") {
    event.preventDefault();
    focusSibling(index, key === "ArrowRight" ? 1 : -1);
    return;
  }
  if (key === "Home" || key === "End") {
    event.preventDefault();
    const indexes = sectionIndexes();
    focusSectionAt(key === "Home" ? indexes[0] : indexes[indexes.length - 1]);
    return;
  }
  if (key === "ArrowUp" || key === "ArrowDown") {
    event.preventDefault();
    const bounds = getSectionBounds(section, values.value);
    setValue(section.type, stepValue(values.value[section.type] ?? null, key === "ArrowUp" ? 1 : -1, bounds));
    typedCount.value = 0;
    typedText.value = "";
    commitLive();
    return;
  }
  if (key === "Backspace" || key === "Delete") {
    event.preventDefault();
    setValue(section.type, null);
    typedCount.value = 0;
    typedText.value = "";
    commitLive();
    return;
  }
  if (/^[0-9]$/.test(key)) {
    event.preventDefault();
    if (section.type === "meridiem") return;
    const bounds = getSectionBounds(section, values.value);
    const result = typeDigit(
      values.value[section.type] ?? null,
      typedCount.value,
      Number(key),
      bounds,
      section.maxDigits,
    );
    setValue(section.type, result.value);
    typedCount.value = result.typed;
    typedText.value = "";
    commitLive();
    if (result.complete) focusSibling(index, 1);
    return;
  }
  // Textual month (`MMM`/`MMMM`): match typed letters against localized month names.
  // Checked before the meridiem A/P handling so "a"/"p" pick April/… here, not AM/PM.
  if (section.type === "month" && section.monthStyle && /^\p{L}$/u.test(key)) {
    event.preventDefault();
    const names = monthNamesLong.value;
    const extended = matchMonthName(typedText.value + key, names);
    const match = extended ?? matchMonthName(key, names);
    typedText.value = extended ? typedText.value + key : match ? key : "";
    if (match) {
      setValue("month", match.month);
      typedCount.value = 0;
      commitLive();
      if (match.complete) focusSibling(index, 1);
    }
    return;
  }
  if (hasMeridiem.value && (key === "a" || key === "A" || key === "p" || key === "P")) {
    event.preventDefault();
    setValue("meridiem", key === "a" || key === "A" ? 0 : 1);
    commitLive();
    return;
  }
  if (SEPARATOR_KEYS.includes(key)) {
    event.preventDefault();
    if (values.value[section.type] != null) focusSibling(index, 1);
    return;
  }
  // Swallow any other printable character — sections only accept the keys above.
  if (key.length === 1 && !event.ctrlKey && !event.metaKey) event.preventDefault();
}

/** Which on-screen keyboard a section asks for. */
function inputModeFor(section: FormatSection): "numeric" | "text" {
  if (section.type === "meridiem") return "text";
  if (section.type === "month" && section.monthStyle) return "text";
  return "numeric";
}

/**
 * On-screen keyboards report a virtual key as `Unidentified` in `keydown` and carry the text in
 * `beforeinput` instead, so replay it as the keystroke it stands for. `keydown` already cancels
 * anything a physical key produces, so this only ever sees input a hardware key did not.
 */
function onSectionBeforeInput(event: InputEvent) {
  event.preventDefault();
  // Every `delete*` variant means the same thing here; anything else is the text that was typed.
  const keys = event.inputType.startsWith("delete") ? ["Backspace"] : [...(event.data ?? "")];
  for (const key of keys) {
    // Re-read the section per key: a completed one hands focus to the next.
    const index = activeIndex.value;
    const part = index === null ? undefined : parts.value[index];
    if (part?.kind !== "section" || index === null) return;
    onSectionKeydown(new KeyboardEvent("keydown", { key }), part, index);
  }
}

function onSectionInput(event: Event) {
  const el = event.target as HTMLInputElement;
  if (el.value !== "") el.value = "";
}

function onRootMouseDown(event: MouseEvent) {
  if (props.disabled) return;
  const target = event.target as HTMLElement;
  if (target.closest('[role="spinbutton"]')) return;
  // Click on padding/separator: keep focus inside and jump to the first open section.
  event.preventDefault();
  focusFirstEditable();
}

defineExpose({ focus: focusFirstEditable, focusLast: focusLastEditable, reset: resetFromModel });
</script>

<style module lang="scss">
@use "../../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;

.root {
  @include FigmaTypoMixins.typo-body-medium;
  display: inline-flex;
  align-items: center;
  white-space: pre;
  color: var(--color-content-primary);
  cursor: text;
  outline: none;

  &.disabled {
    cursor: auto;
  }
}

.sectionWrap {
  position: relative;
  display: inline-block;
  border-radius: 2px;

  &:focus-within {
    background-color: var(--color-semantic-informative-p6);
  }
}

.section {
  &.empty {
    color: var(--color-content-tertiary);
  }
}

.root .sectionWrap input.sectionInput {
  position: absolute;
  inset: 0;
  padding: 0;
  border: 0;
  background-color: transparent;
  color: transparent;
  caret-color: transparent;
  cursor: text;
  outline: none;

  &:not([readonly]) {
    user-select: text;
  }
}

.literal {
  cursor: text;
}
</style>
